import { GaEventData } from "../../types/EventsData";
import { ServiceProvider } from "../../dependencies_management/ServiceProvider";
import { AnalyticsPageNameProvider } from "../../utilities/page_name/AnalyticsPageNameProvider";
import { AnalyticsTracker } from "../AnalyticsTracker";
import { QueryStringValuesAccessor } from "../../utilities/query_string/QueryStringValuesAccessor";
import { AnalyticsFunc } from "../../types/AnalyticsFunc";
import { TrafficInfo } from "../../utilities/traffic_info/TrafficInfo";
import { TrafficInfoProvider } from "../../utilities/traffic_info/TrafficInfoProvider";
import { DocumentWrapper } from "../../utilities/document_wrapping/DocumentWrapper";
import { GoogleAnalyticsDimensionsAndMetrics } from "../../utilities/analytics_parameters/GoogleAnalyticsDimentionsAndMetrics";
import { GoogleAnalyticsDimensionsAndMetricsProvider } from "../../utilities/analytics_parameters/GoogleAnalyticsDimentionsAndMetricsProvider";
import { GoogleAnalyticsTrackerOptions } from "./GoogleAnalyticsTrackerOptions";

const PAGE_VIEW_EVENT = "pageview";
const SEND_COMMAND = "send";

export class GoogleAnalyticsTracker extends AnalyticsTracker<GaEventData> {
	private readonly doesGoogleAnalyticsTrackingUrlExist: boolean;
	private readonly analyticsPageNameProvider: AnalyticsPageNameProvider;
	private readonly queryStringValuesAccessor: QueryStringValuesAccessor;
	private readonly document: DocumentWrapper;
	private readonly trafficInfoProvider: TrafficInfoProvider;
	private readonly dimensionsAndMetricsProvider: GoogleAnalyticsDimensionsAndMetricsProvider;
	private readonly ga: AnalyticsFunc;

	constructor(
		private readonly options: GoogleAnalyticsTrackerOptions,
		readonly serviceProvider: ServiceProvider
	) {
		super();
		this.doesGoogleAnalyticsTrackingUrlExist =
			options.trackingCodes.gaTrackingCodeUrl !== undefined;
		this.ga = options.googleAnalyticsFunctions.ga;
		this.analyticsPageNameProvider =
			serviceProvider.getAnalyticsPageNameProvider();
		this.queryStringValuesAccessor =
			serviceProvider.getQueryStringValuesAccessor();
		this.document = serviceProvider.getDocumentWrapper();
		this.trafficInfoProvider = serviceProvider.getTrafficInfoProvider();
		this.dimensionsAndMetricsProvider =
			serviceProvider.getGoogleAnalyticsDimensionsAndMetricsProvider();
	}

	submitPageView(): void {
		if (
			this.queryStringValuesAccessor.getValueByKey("g") === "checklist" &&
			this.queryStringValuesAccessor.getValueByKey("framed") === "1"
		) {
			return;
		}

		const customDimensionsAndMetrics = this.getCustomDimensionsAndMetrics();
		let analyticsPageName = customDimensionsAndMetrics.pageName;
		let urlPageName = this.document.windowLocation.pathWithQueryString;

		const trafficInfo = this.getTrafficInfo();
		if (trafficInfo.doesSourceExist()) {
			analyticsPageName += trafficInfo.toPageNameFragment();
			urlPageName += trafficInfo.toPageNameFragment();
		}

		this.useCustomDimensionsAndMetrics(customDimensionsAndMetrics);

		if (analyticsPageName !== undefined) {
			this.ga(SEND_COMMAND, PAGE_VIEW_EVENT, analyticsPageName);
		} else {
			this.ga(SEND_COMMAND, PAGE_VIEW_EVENT);
		}

		if (this.doesGoogleAnalyticsTrackingUrlExist) {
			this.ga("urlTracker.send", PAGE_VIEW_EVENT, urlPageName);
		}
	}

	// aka submitGAEvent
	submitEvent(eventData: GaEventData): void {
		this.useCustomDimensionsAndMetrics(this.getCustomDimensionsAndMetrics());

		const {
			category,
			action,
			label = this.getAnalyticsPageName(),
			nonInteraction,
		} = eventData;
		this.ga(
			SEND_COMMAND,
			"event",
			category,
			action,
			label ?? this.getAnalyticsPageName(),
			{
				nonInteraction,
			}
		);
	}

	private getAnalyticsPageName(): string | undefined {
		return this.analyticsPageNameProvider.providePageName(
			this.executionContext
		);
	}

	private getTrafficInfo(): TrafficInfo {
		const dashSeparatedTrafficSourceInfo =
			this.queryStringValuesAccessor.getValueByKey("pid");

		return this.trafficInfoProvider.provideInfo(dashSeparatedTrafficSourceInfo);
	}

	private getCustomDimensionsAndMetrics(): GoogleAnalyticsDimensionsAndMetrics {
		return this.dimensionsAndMetricsProvider.provideDimensionsAndMetrics(
			this.executionContext,
			this.options.isUserLicensed
		);
	}

	// aka setGACustomVars
	// https://developers.google.com/analytics/devguides/collection/analyticsjs/custom-dims-mets
	private useCustomDimensionsAndMetrics(
		customDimensionsAndMetrics: GoogleAnalyticsDimensionsAndMetrics
	): void {
		const {
			semicolonSeparatedExperiments,
			location,
			file,
			doNotTrack,
			isUserLicensed,
			pageName,
		} = customDimensionsAndMetrics;

		const gaCommands = ["set"];

		if (this.doesGoogleAnalyticsTrackingUrlExist) {
			gaCommands.push("urlTracker.set");
		}

		for (const gaCommand of gaCommands) {
			this.ga(gaCommand, "dimension1", semicolonSeparatedExperiments);
			this.ga(gaCommand, "dimension2", location);
			if (file !== undefined) this.ga(gaCommand, "dimension3", file);
			this.ga(gaCommand, "dimension4", isUserLicensed);
			this.ga(gaCommand, "dimension5", this.executionContext.productCode);
			this.ga(gaCommand, "dimension6", doNotTrack);
			if (pageName !== undefined) this.ga(gaCommand, "page", pageName);
		}
	}
}
